import { Fragment, useEffect, useState } from 'react';
import { Typography, Box, Modal, TextareaAutosize } from '@mui/material';

import { getDataForTypingExam } from '../../../../services/configurationService';
import { useToaster } from '../../../../providers/ToasterProvider';
import { deleteAllBySet, upsertTypingQuestion } from '../../../../services/questionService';

import RadiusButton from '../../../../components/RadiusButton';
import mimes from '../../../../constant/mimes';

import DELETE from '../../../../assets/images/action-delete.svg';
import { LoadingButton } from '@mui/lab';

export const TypingUpload = ({ drive, course, configurationId }) => {

    const { addNotification } = useToaster();

    const [configuration, setConfiguration] = useState(null);
    const [sets, setSets] = useState([]);
    const [dataUploaded, setDataUploaded] = useState(false);
    const [deleteModal, setDeleteModal] = useState({
        isVisible: false,
        set: null,
        language: null,
    });
    const [uploadModal, setUploadModal] = useState({
        isVisible: false,
        file: null,
        set: null,
        language: null,
        errors: [],
    });
    const [processing, setIsProcessing] = useState({
        isSaving: false,
        setIndex: null,
        languageIndex: null,
    });


    useEffect(() => {
        if (configurationId) {
            const fetchConfiguration = async () => {
                try {
                    const res = await getDataForTypingExam(configurationId);
                    if (res.code === 'OK') {
                        setConfiguration(res.data.configuration);
                        const data = res.data.sets.map((set) => {
                            set.languages = set.languages.map((lan) => {
                                lan.localContent = lan.content;
                                return lan;
                            })
                            return set;
                        })
                        setSets(data);
                    }
                } catch (error) {
                    console.log('There is an error while getting configuration details', error);
                }
            }
            fetchConfiguration();
        }
    }, [configurationId, dataUploaded]);

    const handleFileDrop = (e, set, language) => {
        const inputFile = e.target.files?.[0];
        if (inputFile && mimes.WORD.includes(inputFile.type)) {
            document.getElementById('descriptive_file').value = '';
            setUploadModal({
                ...uploadModal,
                file: inputFile
            });
        }
    }
    const handleRemoveFile = () => {
        document.getElementById('descriptive_file').value = '';
        setUploadModal({
            ...uploadModal,
            file: null,
        });
    }
    const handleCloseDeleteModal = () => {
        setDeleteModal({
            ...deleteModal,
            isVisible: false,
            set: null,
            language: null
        })
    }
    const handleDeleteAllConfirm = async () => {
        if (!deleteModal.set || !deleteModal.language || !drive?.id || !course?.id) return;
        try {
            const languages = deleteModal.language === 'all' ? configuration.languages : [deleteModal.language];
            const body = {
                driveId: drive.id,
                courseId: course.id,
                set: deleteModal.set,
                languages: languages
            }
            const res = await deleteAllBySet(body);
            if (res.code === 'OK') {
                setSets(
                    sets.map((set) => {
                        if (set.set === deleteModal.set) {
                            if (deleteModal.language === 'all') {
                                set.languages = set.languages.map((lan) => {
                                    lan.count = 0;
                                    return lan
                                })
                            } else {
                                const setLanguageIndex = set.languages.findIndex((lan) => lan.language === deleteModal.language);
                                if (setLanguageIndex > -1) {
                                    set.languages[setLanguageIndex].count = 0;
                                }
                            }
                            set.hasUploadedData = set.languages.find((lan) => lan.count > 0);
                        }
                        return set;
                    })
                );
                addNotification('Content deleted successfully.', 'success');
                setDeleteModal({
                    ...deleteModal,
                    isVisible: false,
                    set: null,
                    language: null
                });
            }
        } catch (error) {
            console.log('There is an error while deleting questions', error);
        }
    }
    const handleCloseUploadModal = () => {
        setUploadModal({
            isVisible: false,
            file: null,
            set: null,
            language: null,
            errors: [],
        });
    }
    const handleChange = (value, setIndex, languageIndex) => {

        setSets(
            sets.map((s, sIndex) => {
                if (sIndex === setIndex) {
                    s.languages = s.languages.map((lan, lanIndex) => {
                        if (lanIndex === languageIndex) {
                            lan.localContent = value;
                        }
                        return lan;
                    })
                }
                return s;
            })
        )
    }
    const handleUploadQuestion = async (setIndex, languageIndex) => {
        const set = sets[setIndex].set;
        const language = sets[setIndex].languages[languageIndex].language;
        const content = sets[setIndex].languages[languageIndex].localContent;
        if (!set || !language || !content) return;

        setIsProcessing({
            isSaving: true,
            setIndex: setIndex,
            languageIndex: languageIndex
        });
        try {
            const response = await upsertTypingQuestion({
                configurationId: configurationId,
                set: set,
                language: language,
                content: content
            });
            if (response.code === 'OK') {
                addNotification('Content saved successfully.', 'success');
                setSets(
                    sets.map((s, sIndex) => {
                        if (sIndex === setIndex) {
                            s.languages = s.languages.map((lan, lanIndex) => {
                                if (lanIndex === languageIndex) {
                                    lan.content = content
                                }
                                return lan;
                            })
                        }
                        return s;
                    })
                );
            }
        } catch (error) {
            console.log('There is an error while saving typing exam content', error);
            const code = error?.error?.code;
            if (code === 'CREATE_FAILED'  || code === 'UNPROCESSABLE_ENTITY') {
                addNotification('Content does not saved.', 'danger');
            } else {
                addNotification('Something bad happened on server.', 'danger');
            }
        } finally {
            setIsProcessing({
                isSaving: false,
                setIndex: null,
                languageIndex: null
            });
        }
    }

    return (
        <Fragment>
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                px: 1.5,
                py: 2,
            }}>
                {sets.map((setDetails, index) => {
                    return <Box key={index} sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                        backgroundColor: 'grey.100',
                        borderRadius: 2,
                        p: 1.5,
                    }}>
                        <Typography variant='h6' component='h2' sx={{
                            color: 'primary.main'
                        }}>Set - {setDetails?.set}</Typography>
                        {setDetails?.languages?.map((lan, lanIndex) => {
                            return <Box key={lanIndex} sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                mb: 1,
                            }}>
                                <Typography variant='h6' component='h6' sx={{
                                    fontSize: 16,
                                    mb: 1,
                                    color: 'primary.main'
                                }}>
                                    Language -&nbsp;
                                    <Box component='span' sx={{ textTransform: 'capitalize' }}>{lan.language}</Box>
                                </Typography>
                                <TextareaAutosize
                                    className='typing-input'
                                    minRows={5}
                                    maxRows={5}
                                    placeholder="Enter content here"
                                    value={lan.localContent}
                                    onChange={(e) => handleChange(e.target.value, index, lanIndex)}
                                />
                                <LoadingButton
                                    disabled={lan.content === lan.localContent}
                                    sx={{ alignSelf: 'flex-end', mt: 0.75, borderRadius: 8 }}
                                    variant='contained'
                                    loading={processing?.isSaving && processing?.setIndex === index && processing?.languageIndex === lanIndex}
                                    onClick={() => handleUploadQuestion(index, lanIndex)}
                                >
                                    {lan.content ? 'Update' : 'Save'}
                                </LoadingButton>
                            </Box>
                        })}
                    </Box>
                })}

            </Box>
            {/* <TableContainer sx={{
                p: 2,
                maxHeight: 'inherit',
                maxWidth: 'inherit',
                borderRadius: 1
            }}>
                <Table stickyHeader aria-label="table">
                    <TableHead>
                        <TableRow>
                            <TableCell
                                align='left'
                                style={{ width: 140 }}
                                sx={{ border: 'none', p: 0 }}
                            ></TableCell>
                            <TableCell
                                align='left'
                                style={{ minWidth: 320 }}
                                sx={{ border: 'none', p: 0 }}
                            ></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sets.map((setDetails, index) => {
                            return <TableRow key={index}>
                                <TableCell sx={{ border: 'none', verticalAlign: 'top' }}>
                                    <Typography component='span' variant='h6' sx={{
                                        fontSize: 18,
                                        fontWeight: 500,
                                        color: 'primary.main'
                                    }}>Set {setDetails.set}</Typography>
                                </TableCell>
                                <TableCell sx={{ border: 'none', py: 1 }}>
                                    {setDetails?.languages?.map((lan, lanIndex) => {
                                        return <Box key={lanIndex} sx={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: 2,
                                            mb: 1,
                                        }}>
                                            <Typography sx={{
                                                textTransform: 'capitalize',
                                                fontWeight: 500,
                                                fontSize: 16,
                                                py: 1,
                                                width: 140
                                            }}>{lan.language}</Typography>
                                            <Box sx={{
                                                flex: 1,
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                // flexDirection: 'column',
                                                gap: 1.5,
                                                py: 1.5,
                                                px: 2.5,
                                                backgroundColor: 'grey.100',
                                                borderRadius: 2,
                                            }}>
                                                {lan.count <= 0 && <RadiusButton
                                                    variant='contained'
                                                    color='secondary'
                                                    onClick={() => onUploadContent(setDetails.set, lan.language)}
                                                >Upload</RadiusButton>}
                                                {(lan.count > 0) && (
                                                    <Typography
                                                        variant='subtitle1'
                                                        component='h6'
                                                    >
                                                        Verified ({lan?.verifiedCount} / {lan?.count})
                                                    </Typography>
                                                )}
                                                {lan?.count > 0 && <>
                                                    <RadiusButton
                                                        variant='contained'
                                                        color='primary'
                                                        onClick={() => handlePreviewQuestion(setDetails.set, lan.language)}
                                                    >Preview & Verify</RadiusButton>
                                                </>}
                                                {(lan?.count > 0) && <RadiusButton
                                                    variant='contained'
                                                    color='secondary'
                                                    onClick={() => handleDeleteAll(setDetails.set, lan.language)}
                                                >Delete All</RadiusButton>}

                                            </Box>
                                        </Box>
                                    })}
                                </TableCell>
                            </TableRow>
                        })}
                    </TableBody>
                </Table>
            </TableContainer> */}
            <Modal
                hideBackdrop={true}
                className='backdrop'
                open={deleteModal.isVisible}
                onClose={handleCloseDeleteModal}
            >
                <Box className='modal-container' sx={{
                    maxWidth: 500,
                    px: 3,
                    py: 6
                }}>
                    <Typography variant="h6" component="h6" sx={{
                        mb: 5,
                        textAlign: 'center'
                    }}>
                        Are you sure you want to delete all the content for
                        <Box component='span' sx={{ color: 'primary.main' }}> Set {deleteModal.set} </Box>
                        in
                        <Box component='span' sx={{ color: 'primary.main', textTransform: 'capitalize' }}> {deleteModal.language} </Box>
                        language?
                    </Typography>

                    <Box sx={{
                        display: 'flex',
                        gap: 5,
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}>
                        <RadiusButton
                            variant='outlined'
                            onClick={handleCloseDeleteModal}
                        >
                            No
                        </RadiusButton>
                        <RadiusButton
                            variant='contained'
                            onClick={handleDeleteAllConfirm}
                        >
                            Yes
                        </RadiusButton>
                    </Box>

                </Box>
            </Modal>
            <Modal
                hideBackdrop={true}
                className='backdrop'
                open={uploadModal.isVisible}
                onClose={handleCloseUploadModal}
            >
                <Box className='modal-container' sx={{
                    maxWidth: 720,
                    p: 3,
                }}>
                    <Typography variant="h6" component="h6" sx={{
                        mb: 2,
                        color: 'primary.main'
                    }}>Upload Content</Typography>
                    <Typography variant="subtitle-1" component="h6" sx={{
                        mb: 2,
                    }}>
                        Uploading descriptive content for
                        <Box component='span' sx={{ color: 'primary.main', mx: 0.5 }}> Set {uploadModal.set}</Box>
                        in
                        <Box component='span' sx={{ color: 'primary.main', textTransform: 'capitalize', mx: 0.5 }}> {uploadModal.language} </Box>
                        language
                        {uploadModal.language === 'all' ? 's' : null}
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.25,
                            mb: 4,
                        }}
                    >
                        <RadiusButton
                            variant='contained'
                            color='secondary'
                            component='label'
                            htmlFor='descriptive_file'
                        >Select file</RadiusButton>
                        <input
                            style={{ display: 'none' }}
                            type='file'
                            id='descriptive_file'
                            onChange={handleFileDrop}
                            accept={mimes.WORD}
                        />
                        {
                            uploadModal?.file && uploadModal?.file?.name
                                ? (
                                    <>
                                        <Box component='span'>{uploadModal.file.name}</Box>
                                        <img
                                            style={{ cursor: 'pointer' }}
                                            src={DELETE}
                                            onClick={handleRemoveFile}
                                            alt=''
                                        />
                                    </>
                                ) : null
                        }
                    </Box>

                </Box>
            </Modal>
        </Fragment>
    );
}